/**
 * Weekly Digest Composer — worker-drafted, persona-tailored, grounded prose.
 *
 * Fixes the auditor's "template-not-Claude digest" finding: the customer
 * weekly digest is drafted by the REAL Argo agent-worker
 * (src/lib/agent/argoWorker.ts `runAgent`, injected — never imported here so
 * the module stays pure and testable), grounded EXCLUSIVELY in
 * {@link DigestGrounding} facts pulled from live records. When no worker is
 * injected — or the worker throws / returns nothing — the composer degrades
 * to a DETERMINISTIC mergeFields-based template and says so honestly via
 * `provenance.deterministic = true`. Either way the draft never fabricates
 * customer data: every grounded item carries the id of the record it came
 * from, and an empty week yields an empty section, not filler.
 *
 * Persona tailoring ({@link tailorForPersona}) adjusts EMPHASIS, not facts:
 * a CFO recipient leads with value delivered and commitments; an admin
 * recipient leads with operational changes and what we need from them.
 *
 * PURE by contract: no store, no firebase, no network except through the
 * injected worker seam, no clock reads unless `nowMs` is omitted.
 */

import type { Account, Deliverable, ProjectTask, RoadmapItem, Ticket } from '../db/types';
import { assembleWeeklyUpdate } from './weeklyUpdate';
import { milestoneRollup } from './milestones';
import { resolveMerge, type MergeContext } from './mergeFields';

// ---------------------------------------------------------------------------
// Grounding
// ---------------------------------------------------------------------------

/** One fact the digest may state, anchored to the record it came from. */
export interface GroundedItem {
  /** Id of the underlying record (deliverable / roadmap / task / ticket). */
  id: string;
  /** Customer-readable one-liner, e.g. "Delivered: Creator pipeline v2". */
  summary: string;
  /** ISO date when the item carries one (due / target date). */
  date?: string;
}

/** Everything true about one account this week — the ONLY drafting input. */
export interface DigestGrounding {
  accountId: string;
  accountName: string;
  /** Deliverables shipped (status delivered / accepted). */
  deliverables: GroundedItem[];
  /** Roadmap milestones whose linked work is 100% complete. */
  milestones: GroundedItem[];
  /** Open work coming up (due soon or queued next). */
  upcoming: GroundedItem[];
  /** Blocked / overdue items that need the customer's input. */
  asks: GroundedItem[];
  /** One honest health line derived from the week's data, or ''. */
  healthTrend: string;
}

/** Live snapshots buildGrounding reads. All optional — empty stays empty. */
export interface GroundingData {
  tasks?: ProjectTask[];
  tickets?: Ticket[];
  deliverables?: Deliverable[];
  roadmap?: RoadmapItem[];
  /** Clock injection for deterministic tests. */
  nowMs?: number;
}

function taskBelongs(task: ProjectTask, account: Account): boolean {
  if (task.accountId) return task.accountId === account.id;
  if (task.accountName) return task.accountName.toLowerCase() === account.name.toLowerCase();
  return false;
}

/** Same honest ticket↔account ties as the canonical weeklyUpdate assembly. */
function ticketBelongs(ticket: Ticket, account: Account, accountTasks: ProjectTask[]): boolean {
  if (accountTasks.some((t) => t.ticketId === ticket.id)) return true;
  const name = account.name.toLowerCase();
  return ticket.labels.some((l) => l === account.id || l.toLowerCase() === name);
}

/**
 * Pull every ledger-backed fact the digest may state: shipped deliverables,
 * milestones hit (via the canonical `milestoneRollup`), upcoming work, and
 * the blocked/overdue asks — plus one honest health line. PURE: reads only
 * its arguments; empty inputs yield empty buckets, never invented items.
 */
export function buildGrounding(account: Account, data: GroundingData): DigestGrounding {
  const nowMs = data.nowMs ?? Date.now();
  const digest = assembleWeeklyUpdate(account, {
    tasks: data.tasks,
    tickets: data.tickets,
    nowMs,
  });

  const accountDeliverables = (data.deliverables ?? []).filter(
    (d) => d.accountId === account.id,
  );
  const deliverables: GroundedItem[] = accountDeliverables
    .filter((d) => d.status === 'delivered' || d.status === 'accepted')
    .map((d) => {
      const item: GroundedItem = {
        id: d.id,
        summary: `${d.status === 'accepted' ? 'Accepted' : 'Delivered'}: ${d.name}`,
      };
      if (d.dueDate) item.date = d.dueDate;
      return item;
    });

  // Milestones hit: roadmap items whose linked account work is 100% done.
  const accountTasks = (data.tasks ?? []).filter((t) => taskBelongs(t, account));
  const accountTickets = (data.tickets ?? []).filter((t) =>
    ticketBelongs(t, account, accountTasks),
  );
  const milestones: GroundedItem[] = [];
  for (const item of data.roadmap ?? []) {
    const rollup = milestoneRollup(item, accountTickets, accountTasks);
    if (!rollup || rollup.total === 0 || rollup.done < rollup.total) continue;
    const grounded: GroundedItem = {
      id: item.id,
      summary: `Milestone reached: ${item.title} (${rollup.done}/${rollup.total} linked items complete)`,
    };
    if (item.targetDate) grounded.date = item.targetDate;
    milestones.push(grounded);
  }

  // Upcoming + asks come straight off the canonical weekly assembly —
  // one bucketing discipline, not a second reimplementation.
  const upcoming: GroundedItem[] = digest.next.map((n, i) => ({
    id: `${account.id}:next:${i}`,
    summary: n.detail ? `${n.title} — ${n.detail}` : n.title,
  }));
  const asks: GroundedItem[] = digest.blockers.map((b, i) => ({
    id: `${account.id}:ask:${i}`,
    summary: b.detail ? `${b.title} — ${b.detail}` : b.title,
  }));

  return {
    accountId: account.id,
    accountName: account.name,
    deliverables,
    milestones,
    upcoming,
    asks,
    healthTrend: digest.healthNote,
  };
}

/** Flatten the grounding in narrative order — the anti-hallucination roster. */
export function allGroundedItems(g: DigestGrounding): GroundedItem[] {
  return [...g.milestones, ...g.deliverables, ...g.upcoming, ...g.asks];
}

// ---------------------------------------------------------------------------
// Personas
// ---------------------------------------------------------------------------

/** Recipient persona the digest is tailored for. */
export type DigestPersona = 'cfo' | 'admin';

/**
 * The persona emphasis line each variant leads with. Exported so tests and
 * the UI can explain exactly what tailoring changed — emphasis, not facts.
 */
export const PERSONA_EMPHASIS: Record<DigestPersona, string> = {
  cfo:
    'For your finance lens: value delivered first — completed milestones and ' +
    'shipped deliverables below, with what is committed next.',
  admin:
    'For day-to-day coordination: what changed operationally this week, and ' +
    'the items where we need your input to keep things moving.',
};

/** Worker persona/instruction text per recipient persona. */
export const PERSONA_INSTRUCTIONS: Record<DigestPersona, string> = {
  cfo:
    'The recipient is a CFO / economic buyer: lead with outcomes and value ' +
    'delivered, keep it brief, tie work to commitments. No operational minutiae.',
  admin:
    'The recipient is a hands-on admin / coordinator: lead with operational ' +
    'changes, be concrete about upcoming work and exactly what you need from them.',
};

// ---------------------------------------------------------------------------
// Drafting — real worker with deterministic mergeFields fallback
// ---------------------------------------------------------------------------

/** Injectable worker seam — the exact `argoWorker.runAgent` call signature. */
export type DigestWorker = (
  agent: { id: string; name: string; template: string; persona: string },
  snapshot: unknown,
) => Promise<{ text: string }>;

/** The agent descriptor the real worker receives. */
export const DIGEST_NARRATOR_AGENT = {
  id: 'weekly-digest-narrator',
  name: 'Weekly Digest Narrator',
  template: 'customer-weekly-digest',
  persona:
    'Warm, concise customer-success writer. Draft a short weekly digest email ' +
    'from the supplied grounding facts ONLY — never invent work, dates, or numbers.',
} as const;

/** Where a draft's prose came from — the honesty flag on every digest. */
export interface DigestProvenance {
  /** true = mergeFields template (worker absent or failed); false = worker prose. */
  deterministic: boolean;
  source: 'worker' | 'fallback';
  /** The persona the draft was tailored for. */
  persona: DigestPersona;
  /** Ids of every grounded record the draft may reference. */
  groundedIn: string[];
  /** Honest one-liner for the UI provenance chip. */
  note: string;
}

/** A drafted (not yet approved) weekly digest. */
export interface DigestDraft {
  accountId: string;
  subject: string;
  body: string;
  provenance: DigestProvenance;
}

/** Merge context built from the account's own fields — never a guess. */
function mergeCtx(account: Account): MergeContext {
  const contacts: MergeContext['contacts'] = [];
  if (account.pocName || account.pocEmail) {
    contacts.push({ name: account.pocName, email: account.pocEmail, title: account.pocTitle });
  }
  for (const c of account.secondaryContacts ?? []) {
    contacts.push({ name: c.name, email: c.email, title: c.title });
  }
  return {
    account: { name: account.name, ownerName: account.ownerName },
    contacts,
    owner: account.ownerName ? { name: account.ownerName } : null,
  };
}

const SUBJECT_TEMPLATE = 'Weekly update — {{account.name}}';

function section(heading: string, items: GroundedItem[]): string {
  if (items.length === 0) return '';
  return `${heading}\n${items.map((i) => `  • ${i.summary}`).join('\n')}`;
}

/**
 * The deterministic fallback body: greeting + honest health line + verbatim
 * grounded bullets + sign-off, all {{tokens}} resolved through the governed
 * mergeFields registry so nothing ever renders "undefined".
 */
export function deterministicDigestBody(
  account: Account,
  grounding: DigestGrounding,
  persona: DigestPersona,
): string {
  const sections = [
    section('Milestones reached', grounding.milestones),
    section('Shipped this week', grounding.deliverables),
    section('Coming up', grounding.upcoming),
    section('Needs your input', grounding.asks),
  ].filter(Boolean);
  const template = [
    'Hi {{contact.name}},',
    PERSONA_EMPHASIS[persona],
    ...(grounding.healthTrend ? [grounding.healthTrend] : []),
    ...(sections.length > 0
      ? sections
      : ['A quiet week on our side — nothing new to report, and nothing blocked.']),
    'Reply any time — we read every message.',
    '— {{owner.name}}',
  ].join('\n\n');
  return resolveMerge(template, mergeCtx(account)).text;
}

/**
 * Draft the weekly digest. Calls the injected agent-worker to write prose
 * grounded in {@link DigestGrounding}; when the worker is absent, throws,
 * or returns nothing, falls back to the deterministic mergeFields template.
 * The provenance flag never lies: `deterministic` is false ONLY when real
 * worker prose made it into the body.
 */
export async function draftDigest(
  account: Account,
  grounding: DigestGrounding,
  persona: DigestPersona,
  worker?: DigestWorker,
): Promise<DigestDraft> {
  const subject = resolveMerge(SUBJECT_TEMPLATE, mergeCtx(account)).text;
  const groundedIn = allGroundedItems(grounding).map((i) => i.id);

  const fallback: DigestDraft = {
    accountId: account.id,
    subject,
    body: deterministicDigestBody(account, grounding, persona),
    provenance: {
      deterministic: true,
      source: 'fallback',
      persona,
      groundedIn,
      note: 'Deterministic template — the agent-worker was unavailable, so this draft was assembled verbatim from the grounded records.',
    },
  };

  if (!worker) return fallback;
  try {
    const { text } = await worker(
      { ...DIGEST_NARRATOR_AGENT },
      {
        account: { id: account.id, name: account.name },
        grounding,
        persona,
        instructions:
          'Write a short weekly digest email body for this customer. Use ONLY ' +
          'the grounding facts provided — never invent work, dates, or numbers. ' +
          PERSONA_INSTRUCTIONS[persona],
      },
    );
    const body = text.trim();
    if (!body) return fallback;
    return {
      accountId: account.id,
      subject,
      body,
      provenance: {
        deterministic: false,
        source: 'worker',
        persona,
        groundedIn,
        note: 'Drafted by the Argo agent-worker from the grounded records.',
      },
    };
  } catch {
    return fallback; // graceful, deterministic — never a blocked draft
  }
}

// ---------------------------------------------------------------------------
// tailorForPersona
// ---------------------------------------------------------------------------

/**
 * Re-emphasize a draft for a persona WITHOUT touching its facts: the prior
 * persona's emphasis line (if present) is removed and the new persona's is
 * prepended, so a CFO and an admin read genuinely different framings of the
 * same grounded content. Pure — returns a new draft, input untouched.
 */
export function tailorForPersona(draft: DigestDraft, persona: DigestPersona): DigestDraft {
  const stripped = draft.body
    .split('\n\n')
    .filter((block) => !Object.values(PERSONA_EMPHASIS).includes(block.trim()))
    .join('\n\n');
  // Keep the greeting first when the body opens with one.
  const blocks = stripped.split('\n\n');
  const hasGreeting = /^(hi|hello|hey)\b/i.test(blocks[0] ?? '');
  const body = hasGreeting
    ? [blocks[0], PERSONA_EMPHASIS[persona], ...blocks.slice(1)].join('\n\n')
    : [PERSONA_EMPHASIS[persona], ...blocks].join('\n\n');
  return {
    ...draft,
    body,
    provenance: { ...draft.provenance, persona },
  };
}
